import React, {useState, useEffect, createContext, useContext, useMemo, useRef} from "react";
import DataGrid, {textEditor, SelectColumn} from "react-data-grid";

import {axiosInstance} from "../../../bricks/axios";
import {useAppData} from "../../../context/AppDataContext";
import {API} from "../../../config/Config";
import useTranslation from "../../../hooks/useTranslation";
import SelectEditor from "../../react-data-grid/editors/SelectEditor";
import moment from 'moment';
import DateEditor from "../../react-data-grid/editors/DateEditor";
import Loading from "../../../bricks/Loading";
import Forms from "../../../bricks/form/Forms";
import UserDetailModalContent from "./UserDetailModalContent";
import ColumnSettings from "../../../bricks/form/ColumnSettingsComponent";
import TestResultModalContent from "./TestResultModalContent";
import {useModal} from "../../../context/ModalContext";

import "./UserAdministration.scss";

const GRID_ID = 'user';
const FilterContext = createContext();

function inputStopPropagation(event) {
    if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.stopPropagation();
    }
}

const UserAdministration = () => {
    const {t} = useTranslation();
    const {
        setError,
        setSuccess,
        selectedPeriod,
        getDataGridColumnsSettings,
        setDataGridColumnsSettings,
        period
    } = useAppData();
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [nationalityList, setNationalityList] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [maxTestPoints, setMaxTestPoints] = useState();
    const [courseMap, setCourseMap] = useState();
    const [statusList, setStatusList] = useState([]);
    const [filters, setFilters] = useState({name: '', surname: '', enabled: true});
    const [courseOptions, setCourseOptions] = useState([]);
    const [sortingTestCount, setSortingTestCount] = useState();
    const [testPointsMap, setTestPointsMap] = useState([]);
    const {openModal} = useModal();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const {method, url} = API.loadUsersOverview;
                const {data} = await axiosInstance[method](url, {params: {selectedPeriod}});
                const {
                    userList,
                    maxTestPoints,
                    nationalityList,
                    statusList,
                    courseList,
                    testPointsMap
                } = data;

                setTestPointsMap(testPointsMap);
                setCourseOptions(courseList.map(course => {
                    return {value: course._id, label: course.code};
                }));
                setCourseMap(courseList.reduce((acc, course) => {
                    acc[course._id] = course;
                    return acc;
                }, {}));
                setMaxTestPoints(maxTestPoints);
                setStatusList(statusList);
                setNationalityList(nationalityList);
                const tmpRows = userList.map((u, i) => ({...u, origIndex: i}));
                setRows(tmpRows);
                setFilteredRows(tmpRows);
                setIsLoading(false);
                setSortingTestCount(period.sortingTestDefinitionList.length);
            } catch (error) {
                setError(error);
                setIsLoading(false);
            }
        };

        selectedPeriod && fetchData();
    }, [selectedPeriod]);

    useEffect(() => {
        if (rows) {
            const newFilteredRows = rows
                ?.filter(row => {
                    return Object.keys(filters).every((key) => {
                            if (key !== "enabled") {
                                if (typeof filters[key] === 'number') {
                                    return row[key] === filters[key];
                                } else if (filters[key]) {
                                    return row[key].toString().toLowerCase().includes(filters[key].toLowerCase());
                                }
                            }
                            return true;
                        }
                    )
                });
            setFilteredRows(newFilteredRows);
        }
    }, [rows, filters]);

    const TestResultEditor = ({row, onClose}) => {
        useEffect(() => {
            handleOpenTestResults(row);
            onClose(true);
        }, []);
        return null;
    };

    const handleFieldUpdate = async (updatedRow, {key}) => {
        try {
            const body = {[key]: updatedRow[key]};
            const {method, url} = API.updateUser(updatedRow._id);
            const {data} = await axiosInstance[method](url, body);
            // update rows
            setRows(prevRows => {
                const newRows = [...prevRows];
                newRows[updatedRow.origIndex] = {...newRows[updatedRow.origIndex], [key]: data[key]};
                return newRows;
            });
            setSuccess(<label>Uživatel <strong>{updatedRow.name} {updatedRow.surname}</strong> úspěšně
                aktualizován.</label>);
        } catch (e) {
            setError(e);
        }
    }

    const deleteTestResult = async (id) => {
        try {
            const {method, url} = API.deleteTestResult(id);
            await axiosInstance[method](url);
            return null;
        } catch (e) {
            throw new Error(`Failed to delete test result: ${e.message}`);
        }
    };

    const updateTestResult = async (id, body) => {
        try {
            const {method, url} = API.updateTestResult(id);
            const {data} = await axiosInstance[method](url, body);
            return data;
        } catch (e) {
            throw new Error(`Failed to update test result: ${e.message}`);
        }
    };

    const createTestResult = async (body) => {
        try {
            body.period = selectedPeriod;
            body.testType = 'sortingTest';
            const {method, url} = API.createTestResult;
            const {data} = await axiosInstance[method](url, body);
            return data;
        } catch (e) {
            throw new Error(`Failed to create test result: ${e.message}`);
        }
    };

    const handleTestFieldUpdate = async (updatedRow, {key}) => {
        try {
            let body = {[key]: updatedRow[key], user: updatedRow._id};

            if (updatedRow.testResult) {
                if (updatedRow[key] === "") {
                    // if testResult obj already exists and new value of test.result is false -> delete testResult object
                    await deleteTestResult(updatedRow.testResult._id);
                    body = null;
                } else {
                    // if testResult obj already exists -> update testResult obj
                    body = await updateTestResult(updatedRow.testResult._id, body);
                }
            } else if (updatedRow[key] !== "") {
                // if testResult obj doesn't exist and new value of test.result is true -> create one
                body = await createTestResult(body);
            }

            // update rows
            setRows(prevRows => {
                const newRows = [...prevRows];
                newRows[updatedRow.origIndex] = {...newRows[updatedRow.origIndex], testResult: body};
                return newRows;
            });

            setSuccess(<label>Test uživatele <strong>{updatedRow.name} {updatedRow.surname}</strong> úspěšně
                aktualizován.</label>);

        } catch (e) {
            setError(e);
        }
    };


    const handleCourseRegistrationUpdate = async (updatedRow, {key}) => {
        try {
            let course = updatedRow[key];
            // handle isAlternate
            if (key.match(/isAlternate|passed|course/) && course === "") {
                course = null;
            }

            let data;
            if (key === "course" && course === null) {
                // delete courseRegistration
                const {method, url} = API.deleteCourseRegByUser(updatedRow._id, selectedPeriod);
                await axiosInstance[method](url);

                // update rows
                setRows(prevRows => {
                    const newRows = [...prevRows];
                    newRows[updatedRow.origIndex] = {...newRows[updatedRow.origIndex], course: null};
                    return newRows;
                });
                setSuccess(<label>Registrace do kurzu
                    uživatele <strong>{updatedRow.name} {updatedRow.surname}</strong> úspěšně
                    smazána.</label>);
            } else {
                // update course registration
                const {method, url} = API.handleCourseRegByUser(updatedRow._id);
                const response = await axiosInstance[method](url, {period: selectedPeriod, course});
                data = response.data;

                // update rows
                setRows(prevRows => {
                    const newRows = [...prevRows];
                    newRows[updatedRow.origIndex] = {...newRows[updatedRow.origIndex], [key]: data[key]};
                    return newRows;
                });
                setSuccess(<label>Registrace do kurzu
                    uživatele <strong>{updatedRow.name} {updatedRow.surname}</strong> úspěšně
                    aktualizována.</label>);
            }
        } catch (e) {
            setError(e);
        }
    }

    const filterColumnClassName = 'filter-cell';

    const renderTestResultCell = ({row}) => {
        let totalResultPercentage, totalTestResult;
        if (row.testResultList?.length) {
            totalTestResult = row.testResultList.reduce((acc, testResult) => acc + testResult.result, 0);
            totalResultPercentage = Math.round(totalTestResult / maxTestPoints * 100);
        }

        return <div style={{display: "flex", alignItems: 'center', justifyContent: 'center'}}>
            {
                row.testResultList ? (
                    <>
                        <progress max={100} value={totalTestResult / maxTestPoints * 100}
                                  style={{inlineSize: 90}}/>
                        &nbsp;{totalResultPercentage}% ({row.testResultList.length}/{sortingTestCount})
                    </>
                ) : ''
            }
        </div>;
    }

    const columns = useMemo(() => [
        SelectColumn,
        {
            key: "name",
            name: "Jméno",
            renderEditCell: textEditor,
            minWidth: 170,
            frozen: true,
            editable: true,
            updateFn: handleFieldUpdate,
            headerCellClass: filterColumnClassName,
            renderCell: ({row}) => <label className="name"><Forms.DetailIcon
                onClick={() => handleModalOpen(row._id)}/>{row.name}</label>,
            renderHeaderCell: (p) => (
                <FilterRenderer {...p}>
                    {({filters, ...rest}) => (
                        <input
                            {...rest}
                            value={filters[p.column.key]}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    [p.column.key]: e.target.value
                                })
                            }
                            onKeyDown={inputStopPropagation}
                        />
                    )}
                </FilterRenderer>
            ),
        },
        {
            key: "surname",
            name: "Příjmení",
            renderEditCell: textEditor,
            minWidth: 170,
            frozen: true,
            editable: true,
            updateFn: handleFieldUpdate,
            headerCellClass: filterColumnClassName,
            renderHeaderCell: (p) => (
                <FilterRenderer {...p}>
                    {({filters, ...rest}) => (
                        <input
                            {...rest}
                            value={filters[p.column.key]}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    [p.column.key]: e.target.value
                                })
                            }
                            onKeyDown={inputStopPropagation}
                        />
                    )}
                </FilterRenderer>
            )
        },
        {
            key: "email",
            name: "E-mail",
            minWidth: 200,
            resizable: true,
            renderEditCell: textEditor,
            updateFn: handleFieldUpdate,
            editable: true,
            headerCellClass: filterColumnClassName,
            renderHeaderCell: (p) => (
                <FilterRenderer {...p}>
                    {({filters, ...rest}) => (
                        <input
                            {...rest}
                            value={filters[p.column.key]}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    [p.column.key]: e.target.value
                                })
                            }
                            onKeyDown={inputStopPropagation}
                        />
                    )}
                </FilterRenderer>
            )
        },
        {
            key: "mobile",
            name: "Mobil",
            minWidth: 120,
            resizable: true,
            renderEditCell: textEditor,
            updateFn: handleFieldUpdate,
            editable: true,
            headerCellClass: filterColumnClassName,
            renderHeaderCell: (p) => (
                <FilterRenderer {...p}>
                    {({filters, ...rest}) => (
                        <input
                            {...rest}
                            value={filters[p.column.key]}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    [p.column.key]: e.target.value
                                })
                            }
                            onKeyDown={inputStopPropagation}
                        />
                    )}
                </FilterRenderer>
            )
        },
        {
            key: "nationality",
            name: "Národnost",
            minWidth: 100,
            renderEditCell: (props) => <SelectEditor options={nationalityOptions} {...props}/>,
            updateFn: handleFieldUpdate
        },
        {
            key: "residenceStatus",
            name: "Status pobytu",
            minWidth: 130,
            renderCell: ({row}) => t(row.residenceStatus),
            renderEditCell: (props) => <SelectEditor options={statusOptions} {...props}/>,
            updateFn: handleFieldUpdate
        },
        {
            key: "varSymbol",
            name: "Var. symbol",
            minWidth: 90,
            headerCellClass: filterColumnClassName,
            renderHeaderCell: (p) => (
                <FilterRenderer {...p}>
                    {({filters, ...rest}) => (
                        <input
                            {...rest}
                            value={filters[p.column.key]}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    [p.column.key]: e.target.value
                                })
                            }
                            onKeyDown={inputStopPropagation}
                        />
                    )}
                </FilterRenderer>
            )
        },
        {
            key: "dateOfBirth",
            name: "Datum narození",
            minWidth: 120,
            renderCell: ({row}) => moment(row.dateOfBirth).format('DD.MM.YYYY'),
            renderEditCell: (props) => <DateEditor {...props}/>,
            updateFn: handleFieldUpdate
        },
        {
            key: "sex",
            name: "Pohlaví",
            minWidth: 80,
            renderCell: ({row}) => t(row.sex),
            editable: true,
            renderEditCell: props => <SelectEditor options={sexOptions} {...props}/>,
            updateFn: handleFieldUpdate
        },
        {
            key: "course",
            name: "Kurz",
            minWidth: 150,
            renderCell: ({row}) => courseMap[row.course]?.code,
            renderEditCell: props => <SelectEditor options={courseOptions} {...props} emptyOption/>,
            updateFn: handleCourseRegistrationUpdate // tato funkce není dodělaná
        },
        {
            key: "result",
            name: "Výsledek z testu",
            minWidth: 150,
            renderCell: renderTestResultCell,
            editable: false,
            updateFn: handleTestFieldUpdate,
            renderEditCell: (props) => <TestResultEditor {...props} />,
        },
        {
            key: "note",
            name: "Poznámka",
            renderEditCell: textEditor,
            editable: true,
            updateFn: handleFieldUpdate
        }
    ].map(i => ({...i, resizable: true})), [maxTestPoints]);

    const handleSingleTestPointsChange = async (userId, trIdOrNull, testIndex, newPointsOrNull) => {
        try {
            // DELETE
            if (trIdOrNull && newPointsOrNull === null) {
                await deleteTestResult(trIdOrNull);

                // 1) grid
                setRows(prevRows => {
                    // if (!testResultsUser) return prevRows;
                    const idx = prevRows.findIndex(r => r._id === userId);
                    if (idx === -1) return prevRows;

                    const newRows = [...prevRows];
                    const oldRow = newRows[idx];
                    const newList = (oldRow.testResultList || []).filter(item => item._id !== trIdOrNull);
                    newRows[idx] = {...oldRow, testResultList: newList};
                    return newRows;
                });

                setSuccess(`Výsledek testu #${testIndex} byl odstraněn`);
                return {deleted: true, _id: trIdOrNull, testIndex};
            }

            // CREATE
            if (!trIdOrNull && typeof newPointsOrNull === "number") {
                const created = await createTestResult({
                    user: userId,
                    testIndex,
                    result: newPointsOrNull
                });

                // 1) modál
                // setTestResults(prev => [...prev, created]);

                // 2) grid
                setRows(prevRows => {
                    // if (!testResultsUser) return prevRows;
                    const idx = prevRows.findIndex(r => r._id === userId);
                    if (idx === -1) return prevRows;

                    const newRows = [...prevRows];
                    const oldRow = newRows[idx];
                    const newList = [...(oldRow.testResultList || []), created];
                    newRows[idx] = {...oldRow, testResultList: newList};
                    return newRows;
                });

                setSuccess(`Výsledek testu #${testIndex} byl vytvořen`);
                return created;
            }

            // UPDATE
            if (trIdOrNull && typeof newPointsOrNull === "number") {
                const updated = await updateTestResult(trIdOrNull, {result: newPointsOrNull});

                // 1) grid
                setRows(prevRows => {
                    // if (!testResultsUser) return prevRows;
                    const idx = prevRows.findIndex(r => r._id === userId);
                    if (idx === -1) return prevRows;

                    const newRows = [...prevRows];
                    const oldRow = newRows[idx];
                    const newList = (oldRow.testResultList || []).map(item =>
                        item._id === trIdOrNull ? {...item, result: updated.result} : item
                    );
                    newRows[idx] = {...oldRow, testResultList: newList};
                    return newRows;
                });

                setSuccess(`Výsledek testu #${testIndex} byl upraven`);
                return updated;
            }
        } catch (e) {
            setError(e);
        }
    };

    const dataGridColumnsSettings = getDataGridColumnsSettings(GRID_ID);
    const [columnSettings, setColumnSettings] = useState(columns.map(column => {
        return {
            key: column.key,
            label: column.name,
            visible: dataGridColumnsSettings?.[column.key] ? dataGridColumnsSettings[column.key].visible : true
        };
    }));

    const handleColumnsChange = (updatedColumns) => {
        setDataGridColumnsSettings(GRID_ID, updatedColumns);
        setColumnSettings(updatedColumns);
    };

    const visibleColumnsList = useMemo(() => {
        const visibleColumns = Object.fromEntries(
            columnSettings.map(({key, visible}) => [key, visible])
        );
        return columns.filter(column => !column.key || visibleColumns[column.key] !== false);
    }, [columnSettings, columns]);

    const FilterRenderer = ({tabIndex, column, children}) => {
        const filters = useContext(FilterContext);

        return (
            <>
                <div>{column.name}</div>
                {filters.enabled && <div>{children({tabIndex, filters})}</div>}
            </>
        );
    }

    const sexOptions = [
        {value: 'female', label: t('female')},
        {value: 'male', label: t('male')},
    ];
    const nationalityOptions = nationalityList.map(item => ({value: item.nationality, label: item.nationality}));
    const statusOptions = statusList.map(item => ({value: item.type, label: t(item.type)}));

    const visibleColsSignature = useMemo(
        () => visibleColumnsList.map(c => c.key).join('|'),
        [visibleColumnsList]
    );

    const renderContent = () => {
        if (isLoading) {
            return <Loading/>;
        } else {
            const updateRow = (updatedRows, {column, indexes}) => {
                column.updateFn(updatedRows[indexes[0]], column, indexes[0]);
            }

            return (
                <>
                    <FilterContext.Provider value={filters}>
                        <DataGrid
                            onCellClick={({column, row}) => {
                                if (column.key === 'result') {
                                    handleOpenTestResults(row);
                                }
                            }}
                            key={`${GRID_ID}-${visibleColsSignature}`}
                            className="rdg-light bordered"
                            style={{height: 700}}
                            rowKeyGetter={row => row._id}
                            columns={visibleColumnsList}
                            rows={filteredRows}
                            selectedRows={selectedRows}
                            onSelectedRowsChange={setSelectedRows}
                            onRowsChange={updateRow}
                            headerRowHeight={filters.enabled ? 70 : undefined}
                        />
                    </FilterContext.Provider>
                </>
            );
        }
    };

    const handleUpdateUser = (action, userData) => {
        const newRows = rows.map(x => ({...x}));
        if (action === "add") {
            newRows.push(userData);
        } else {
            const index = newRows.findIndex(user => user._id === userData._id);
            if (action === "delete") {
                newRows.splice(index, 1);
            } else {
                newRows[index] = {...newRows[index], ...userData};
            }
        }
        setRows(newRows);

        const newFilteredRows = newRows
            ?.filter(row => {
                return Object.keys(filters).every((key) => {
                        if (key !== "enabled") {
                            if (typeof filters[key] === 'number') {
                                return row[key] === filters[key];
                            } else if (filters[key]) {
                                return row[key].toString().toLowerCase().includes(filters[key].toLowerCase());
                            }
                        }
                        return true;
                    }
                )
            });
        setFilteredRows(newFilteredRows);
    }

    const handleModalOpen = (userId = null) => {
        openModal(UserDetailModalContent, {
            nationalityOptions,
            statusOptions,
            updateUser: handleUpdateUser,
            userId
        }, {
            title: userId ? "Detail uživatele" : "Vytvořit uživatele"
        });
    }

    const handleOpenTestResults = (row) => {
        const testResultList = Array.isArray(row.testResultList) ? row.testResultList.map(x => ({...x})) : [];
        openModal(TestResultModalContent, {
            testResultList,
            testDefinitionList: period.sortingTestDefinitionList,
            testPointsMap,
            onChangePoints: handleSingleTestPointsChange,
            userId: row._id,
            courseCode: courseMap[row.course]?.code
        }, {
            title: `Výsledky testů uživatele ${row.name} ${row.surname}`
        });
    };

    return (
        <div className="UserAdministration">
            <div className="header">
                <h2>Uživatelé {!isLoading && ` celkem: ${rows.length}`}</h2>
                <div className="controls">
                    <Forms.DropdownMenu
                        items={[
                            {label: "Přidat uživatele", onClick: () => handleModalOpen()},
                            ColumnSettings({
                                gridId: GRID_ID,
                                columns: columnSettings,
                                onColumnsChange: handleColumnsChange
                            })
                        ]}
                    />
                </div>
            </div>
            <div className="dataGrid">
                {renderContent()}
            </div>
        </div>
    );
};

export default UserAdministration;